import {
  Table,
  Button,
  Card,
  Statistic,
  Skeleton,
  message,
  Space,
  Col,
  Descriptions,
  Row,
  Popover,
  Tag,
  Dropdown,
  Popconfirm,
  Tooltip,
  Modal,
  Select,
} from "antd";
import {
  useCreateDebtorPacketMutation,
  useFetchDebtorsQuery,
  useLinkDebtorMutation,
  useUnlinkDebtorMutation,
  useUpdatePacketMainDebtorMutation,
} from "features/debtorProfile/debtorProfileAPI";
import { useEffect, useMemo, useState } from "react";
import { useDebtorSearchCriteriaOptions } from "features/debtorProfile/hooks/useDebtorSearchCriteriaOptions";
import { formatCurrency } from "common/utils";
import currency from "currency.js";
import {
  EllipsisOutlined,
  QuestionCircleOutlined,
  SplitCellsOutlined,
  MergeCellsOutlined,
} from "@ant-design/icons";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import {
  DebtorSearchResultLabel,
  isNewDebtorSearchLayoutEnabled,
} from "components/debtorSearchResultLabel";
import { SearchWithDropdown } from "components/searchWithDropdown";
import {
  useFetchAccountsQuery,
  useLazySearchDebtorsByTypeQuery,
  usePostMergeDebtorsMutation,
} from "features/accountsTable/agencyPortal/accountsTableAPI";
import styled from "styled-components";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import moment from "moment-timezone";
import { useParams } from "react-router-dom";

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledInfoValue = styled.div`
  margin-left: 4px;
`;

const ButtonWithMargin = styled(Button)`
  margin: 0px 10px;
  &:hover {
    background: #f0f0f0;
  }
`;

const StyledActionContainer = styled(Row)`
  background: #fff;
  height: 48px;
  border-top: 1px solid #f0f0f0;
  border-left: 1px solid #f0f0f0;
  border-right: 1px solid #f0f0f0;
  align-items: center;
  flex: 1;
  min-width: 200px;
  display: flex;
  justify-content: space-between;
`;

const StyledActions = styled(Row)`
  margin-left: 47px;
  border-left: 1px solid #f0f0f0;
  height: 100%;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const StyledDiv = styled.div`
  display: flex;
  flex-wrap: nowrap;
`;

const StyledCol = styled(Col)`
  overflow: auto;
`;

export default function PacketedDebtorsTable({ packetId }) {
  const { debtorId: currentDebtorId } = useParams();
  const [createDebtorPacket] = useCreateDebtorPacketMutation();
  const { data: packetedDebtors, isLoading } = useFetchDebtorsQuery(
    {
      filters: {
        debtorPacketIds: [packetId],
      },
    },
    {
      skip: !packetId,
    },
  );
  const { data: packetedAccounts, isLoading: isPacketedAccountsLoading } = useFetchAccountsQuery(
    {
      filters: {
        debtorPacketIds: [packetId],
      },
    },
    {
      skip: !packetId,
    },
  );
  const { data: constants, isLoading: isConstantsLoading } = useFetchBackendConstantsQuery();
  const { options: searchCriteriaOptions, isLoading: isDebtorCustomFieldsLoading } =
    useDebtorSearchCriteriaOptions(constants?.debtorSearchCriteria);

  const [updatePacketMainDebtor, { isLoading: isUpdatingPacketMainDebtor }] =
    useUpdatePacketMainDebtorMutation();
  const [mergeDebtors, { isLoading: isMergingDebtors }] = usePostMergeDebtorsMutation();

  const {
    data: {
      [PERMISSIONS.DEBTOR_PACKET__UNLINK_DEBTOR]: isDebtorPacketUnlinkDebtorAuthorized,
      [PERMISSIONS.DEBTOR_PACKET__UPDATE]: isDebtorPacketUpdateAuthorized,
      [PERMISSIONS.DEBTOR_PACKET__CREATE]: _isDebtorPacketCreateAuthorized,
      [PERMISSIONS.DEBTOR_PACKET__LINK_DEBTOR]: _isDebtorPacketLinkDebtorAuthorized,
      [PERMISSIONS.ACCOUNT_WORKLIST__MERGE_ACCOUNTS]: isMergeAccountsAuthorized,
    },
  } = useAuthorizations();
  const [unlinkDebtor, { isLoading: isUnlinkingDebtor }] = useUnlinkDebtorMutation();
  const [linkDebtor, { isLoading: isLinkingDebtor }] = useLinkDebtorMutation();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isMergeDebtorsModalVisible, setMergeDebtorsModalVisible] = useState(false);
  const [selectedPrimaryDebtor, setSelectedPrimaryDebtor] = useState(null);
  const [isMergeDebtorsButtonEnabled, setMergeDebtorsButtonEnabled] = useState(false);

  const handleUnlinkDebtor = async (debtorId) => {
    const result = await unlinkDebtor({ debtorPacketId: packetId, debtorIds: [debtorId] });
    if ("data" in result) {
      message.success("Debtor unlinked from packet successfully");
      setSelectedRows([]);
    }
  };

  const handleBulkUnlinkDebtors = async () => {
    if (selectedRows.length === 0) return;
    const debtorIds = selectedRows.map((row) => row.id);
    const result = await unlinkDebtor({ debtorPacketId: packetId, debtorIds });
    if ("data" in result) {
      message.success(
        `${debtorIds.length} ${
          debtorIds.length === 1 ? "debtor" : "debtors"
        } unlinked from packet successfully`,
      );
      setSelectedRows([]);
    }
  };

  const onSelectedDebtorChange = (_, selectedRowsData) => {
    setSelectedRows(selectedRowsData);
  };

  const rowSelection = {
    selectedRowKeys: selectedRows.map((row) => row.id),
    onChange: onSelectedDebtorChange,
    preserveSelectedRowKeys: true,
  };

  const hasSelected = selectedRows.length > 0;

  useEffect(() => {
    const debtorIds = selectedRows.map((row) => row.id);
    const uniqueDebtorIds = new Set(debtorIds);
    setMergeDebtorsButtonEnabled(debtorIds.length > 1 && uniqueDebtorIds.size === debtorIds.length);
  }, [selectedRows]);

  const onMergeClick = () => {
    setMergeDebtorsModalVisible(true);
  };

  const onMergeDebtorsCancel = () => {
    setMergeDebtorsModalVisible(false);
    setSelectedPrimaryDebtor(null);
  };

  const onMergeDebtorsSubmit = async () => {
    if (!selectedPrimaryDebtor?.id) return;

    const selectedDebtorIds = selectedRows
      .map((row) => row.id)
      .filter((id) => id !== selectedPrimaryDebtor.id);

    const result = await mergeDebtors({
      primaryDebtorId: selectedPrimaryDebtor.id,
      secondaryDebtorIds: selectedDebtorIds,
    });
    if ("data" in result) {
      message.success("Debtors merged successfully!");
      setSelectedRows([]);
      setMergeDebtorsModalVisible(false);
      setSelectedPrimaryDebtor(null);
    }
  };

  const mergeDebtorOptions = selectedRows.map((row) => ({
    id: row.id,
    name: row.name,
  }));

  const handlePromoteToMainDebtor = async (debtorId) => {
    const result = await updatePacketMainDebtor({
      debtorPacketId: packetId,
      mainDebtorId: debtorId,
    });
    if ("data" in result) {
      message.success("Debtor promoted to main debtor successfully");
    }
  };

  // Calculate packet totals
  const packetTotals = useMemo(() => {
    if (!packetedDebtors) return { totalBalance: 0, totalLawsuits: 0, totalLawsuitsBalance: 0 };

    const uniqueLawsuitIds = new Set();
    packetedDebtors.results?.forEach((debtor) => {
      debtor.lawsuitIds?.forEach((lawsuitId) => {
        uniqueLawsuitIds.add(lawsuitId);
      });
    });
    const totalLawsuits = uniqueLawsuitIds.size;
    const totalLawsuitsBalance = formatCurrency(
      packetedDebtors.results?.reduce(
        (sum, debtor) => sum.add(debtor.accountsCurrentTotalLegalBalance),
        currency(0),
      ) || currency(0),
    );
    const totalBalance = formatCurrency(
      packetedAccounts?.results?.reduce(
        (sum, account) => sum.add(account.totalBalance),
        currency(0),
      ) || currency(0),
    );

    return { totalBalance, totalLawsuits, totalLawsuitsBalance };
  }, [packetedDebtors, packetedAccounts]);

  const handleDebtorClick = (debtorId) => {
    window.open(`/debtors/${debtorId}`, "_blank");
  };

  const debtorColumns = [
    {
      title: "Debtor Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text, record) => (
        <Space align="center">
          <Button type="link" onClick={() => handleDebtorClick(record.id)}>
            {text || "-"}
          </Button>
          {record.isPacketMainDebtor ? <Tag color="blue">MAIN</Tag> : null}
        </Space>
      ),
    },
    {
      title: "External ID",
      dataIndex: "externalId",
      width: 300,
      key: "externalId",
    },
    {
      title: "# of Accounts",
      width: 50,
      render: (text, record) => record.accountIds?.length || 0,
    },
    {
      title: "Total Balance",
      key: "totalBalance",
      render: (text, record) => {
        const balanceItems = [
          {
            key: "principal",
            label: "Principal",
            value: formatCurrency(record.accountsCurrentBalanceBreakdown?.principalBalance),
          },
          {
            // NOTE: We decided to sum up agency/client/prejudgment interest here as "totalInterestBalance", instead of showing them separately
            key: "interest",
            label: "Interest",
            value: formatCurrency(record.accountsCurrentBalanceBreakdown?.totalInterestBalance),
          },
          {
            key: "totalFees",
            label: "Fees",
            value: formatCurrency(record.accountsCurrentBalanceBreakdown?.totalFeesBalance),
          },
          {
            key: "total",
            label: <strong>Total</strong>,
            value: <strong>{formatCurrency(record.accountsCurrentTotalBalance)}</strong>,
          },
        ];
        const totalBalanceContent = (
          <Col>
            <Descriptions bordered column={1} size="small">
              {balanceItems.map((item) => (
                <Descriptions.Item key={item.key} label={item.label}>
                  {item.value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
        );
        return (
          <Row align="middle">
            <StyledInfoValue>{formatCurrency(record.accountsCurrentTotalBalance)}</StyledInfoValue>
            <Popover
              content={totalBalanceContent}
              title="Total Balance Breakdown"
              trigger="hover"
              placement="right"
            >
              <StyledQuestionCircleOutlined />
            </Popover>
          </Row>
        );
      },
    },
    {
      title: "# of Lawsuits",
      width: 50,
      key: "lawsuitIds",
      render: (text, record) => record.lawsuitIds?.length || 0,
    },
    {
      title: "Total Legal Balance",
      key: "totaLegalBalance",
      render: (text, record) => formatCurrency(record.accountsCurrentTotalLegalBalance),
    },
    {
      title: "Actions",
      fixed: "right",
      align: "center",
      render: (text, record) => {
        const isMainDebtor = record.isPacketMainDebtor;

        const menuItems = [
          isDebtorPacketUnlinkDebtorAuthorized
            ? {
                key: "unlink",
                label: (
                  <Popconfirm
                    title="Are you sure you want to unlink this debtor from the packet?"
                    onConfirm={() => handleUnlinkDebtor(record.id)}
                    okText="Yes"
                    cancelText="No"
                    okType="danger"
                  >
                    <span>Unlink from Packet</span>
                  </Popconfirm>
                ),
              }
            : undefined,
          isDebtorPacketUpdateAuthorized
            ? {
                key: "promote",
                label: (
                  <Popconfirm
                    title="Are you sure you want to promote this debtor to main debtor?"
                    onConfirm={() => handlePromoteToMainDebtor(record.id)}
                    okText="Yes"
                    cancelText="No"
                    disabled={isMainDebtor}
                  >
                    <span style={isMainDebtor ? { opacity: 0.5, cursor: "not-allowed" } : {}}>
                      Promote to Main debtor
                    </span>
                  </Popconfirm>
                ),
                disabled: isMainDebtor,
              }
            : undefined,
        ].filter(Boolean);

        return (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <Button type="text" icon={<EllipsisOutlined />} />
          </Dropdown>
        );
      },
    },
  ];

  const handleLinkDebtorToPacket = async (debtorId) => {
    let result;
    if (!packetId) {
      result = await createDebtorPacket({
        debtorIds: [debtorId, parseInt(currentDebtorId, 10)],
        mainDebtorId: parseInt(currentDebtorId, 10),
      });
      if ("data" in result) {
        message.success("Debtor packet created successfully");
      }
    } else {
      result = await linkDebtor({ debtorPacketId: packetId, debtorIds: [debtorId] });
      if ("data" in result) {
        message.success("Debtor linked to packet successfully");
      }
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <SearchWithDropdown
        // @ts-ignore
        initialValues={{ queryFieldType: "consumer_name" }}
        searchHook={useLazySearchDebtorsByTypeQuery}
        selectProps={{
          loading: isConstantsLoading || isDebtorCustomFieldsLoading,
          options: searchCriteriaOptions,
        }}
        searchResultGetter={(debtor, index) => ({
          label: isNewDebtorSearchLayoutEnabled ? (
            <Row justify="space-between" align="middle" wrap={false}>
              <DebtorSearchResultLabel debtor={debtor} />
              <Button type="link" onClick={() => handleLinkDebtorToPacket(debtor.id)}>
                Add to Packet
              </Button>
            </Row>
          ) : (
            <>
              <span>
                <DebtorSearchResultLabel debtor={debtor} />
              </span>
              <Button type="link" onClick={() => handleLinkDebtorToPacket(debtor.id)}>
                Add to Packet
              </Button>
            </>
          ),
          key: index,
        })}
        inputProps={{
          placeholder: "Find debtors to add to this packet",
          title: "Find debtors to add to this packet",
        }}
      />
      {/* Packet Summary */}
      {isLoading || isPacketedAccountsLoading ? (
        <Skeleton loading={isLoading} active />
      ) : (
        <Card>
          <Space direction="horizontal" align="center" size="large">
            <Statistic title="Total Debtors" value={packetedDebtors?.results?.length || 0} />
            <Statistic title="Total Balance" value={packetTotals.totalBalance} />
            <Statistic title="Total Lawsuits" value={packetTotals.totalLawsuits} />
            <Statistic title="Total Lawsuit Balance" value={packetTotals.totalLawsuitsBalance} />
          </Space>
        </Card>
      )}

      {/* Bulk Actions Bar and Table */}
      <Row wrap={false}>
        <StyledCol>
          <Row wrap={false}>
            <StyledActionContainer>
              <StyledActions wrap={false}>
                <StyledDiv>
                  {isDebtorPacketUnlinkDebtorAuthorized && (
                    <Popconfirm
                      title={`Are you sure you want to unlink ${selectedRows.length} ${
                        selectedRows.length === 1 ? "debtor" : "debtors"
                      } from the packet?`}
                      onConfirm={handleBulkUnlinkDebtors}
                      okText="Yes"
                      cancelText="No"
                      okType="danger"
                      disabled={!hasSelected}
                    >
                      <Tooltip
                        title={
                          !hasSelected
                            ? "Select debtor(s) to unlink"
                            : `Unlink ${selectedRows.length} ${
                                selectedRows.length === 1 ? "debtor" : "debtors"
                              }`
                        }
                      >
                        <ButtonWithMargin
                          shape="circle"
                          icon={<SplitCellsOutlined />}
                          type="text"
                          disabled={!hasSelected}
                        />
                      </Tooltip>
                    </Popconfirm>
                  )}
                  {isMergeAccountsAuthorized && (
                    <Tooltip title={`Merge ${selectedRows.length} Debtors`}>
                      <ButtonWithMargin
                        shape="circle"
                        icon={<MergeCellsOutlined />}
                        type="text"
                        disabled={!isMergeDebtorsButtonEnabled}
                        onClick={onMergeClick}
                      />
                    </Tooltip>
                  )}
                </StyledDiv>
              </StyledActions>
            </StyledActionContainer>
          </Row>
          {/* Debtors Table */}
          <Table
            bordered
            loading={
              isLoading || isUnlinkingDebtor || isLinkingDebtor || isUpdatingPacketMainDebtor
            }
            scroll={{ x: "max-content" }}
            // @ts-ignore
            columns={debtorColumns}
            rowSelection={rowSelection}
            dataSource={
              packetedDebtors?.results
                ? [...packetedDebtors.results].sort((a, b) => {
                    if (a.isPacketMainDebtor) return -1;
                    if (b.isPacketMainDebtor) return 1;
                    if (!a.oldestAccountTurnoverDate) return 1;
                    if (!b.oldestAccountTurnoverDate) return -1;
                    return moment(a.oldestAccountTurnoverDate).diff(
                      moment(b.oldestAccountTurnoverDate),
                    );
                  })
                : []
            }
            rowKey="id"
            pagination={{ defaultPageSize: 100 }}
          />
          {isMergeDebtorsModalVisible && (
            <Modal
              maskClosable={false}
              okButtonProps={{
                loading: isMergingDebtors,
                disabled: isMergingDebtors,
              }}
              title="Merge Debtors"
              open={isMergeDebtorsModalVisible}
              onOk={onMergeDebtorsSubmit}
              onCancel={onMergeDebtorsCancel}
            >
              <span style={{ marginRight: "8px" }}>Debtor name to use:</span>
              <Select
                popupMatchSelectWidth={false}
                placeholder="Select a name..."
                value={selectedPrimaryDebtor?.name}
                onChange={(value) =>
                  setSelectedPrimaryDebtor(mergeDebtorOptions.find((debtor) => debtor.id === value))
                }
                options={mergeDebtorOptions.map((debtor) => ({
                  value: debtor.id,
                  label: `${debtor.name} (ID: ${debtor.id})`,
                }))}
              />
            </Modal>
          )}
        </StyledCol>
      </Row>
    </Space>
  );
}
